'use client';

import { useContext } from 'react';
import { ModalContext } from '../context/ModalContext';

/**
 * Provides access to the global modal system.
 *
 * Must be called inside a component that is a descendant of `ModalProvider`.
 * Returns `{ setModal }` — pass any ReactNode to open a modal, or `null` to
 * close the currently open one.
 *
 * The modal is rendered as a React portal into `<body>` and wrapped in a
 * `ModalBackdrop`. Clicking the backdrop calls `setModal(null)` automatically,
 * so modals should call `e.stopPropagation()` on their root element if they
 * need to prevent accidental closure on inner clicks.
 *
 * @throws {Error} When used outside of `ModalProvider`.
 *
 * @example
 * ```tsx
 * import { useModal } from '@/lib/hooks/useModal';
 *
 * const MyComponent = () => {
 *   const { setModal } = useModal();
 *
 *   const openModal = () => {
 *     setModal(
 *       <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
 *         <button
 *           className={styles.closeButton}
 *           onClick={() => setModal(null)}
 *           aria-label="Close"
 *         >
 *           ×
 *         </button>
 *         <div className={styles.modalContent}>
 *           <h2>Modal Title</h2>
 *           <p>Your modal content here</p>
 *         </div>
 *       </div>
 *     );
 *   };
 *
 *   return <button onClick={openModal}>Open Modal</button>;
 * };
 * ```
 */
export function useModal() {
  const context = useContext(ModalContext);

  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }

  return context;
}
